import React, { useState, useEffect, useCallback } from 'react';
import { getStellungnahmen, updateStellungnahmeStatus } from '../api';
import StellungnahmeForm from './forms/StellungnahmeForm';
import StellungnahmeEditDialog from './forms/StellungnahmeEditDialog';
import StellungnahmeDetailDialog from './stellungnahmen/StellungnahmeDetailDialog';

function Stellungnahmen({ user, onNavigateToCustomer }) {
  const [stellungnahmen, setStellungnahmen] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [selectedStellungnahme, setSelectedStellungnahme] = useState(null);

  // Berechtigungen
  const isAdmin = user?.isAdmin || false;
  const isOrgLeader = user?.isOrgLeader || false;
  const isCallcenterLeader = user?.isCallcenterLeader || false;
  const isTeamleader = user?.isTeamlead || false;
  const isFachberater = user?.isFachberater || false;
  const canUpdateStatus = isAdmin || isOrgLeader || isCallcenterLeader;
  const canEditStellungnahme = isCallcenterLeader || isOrgLeader; // Nur Callcenter Leader können bearbeiten

  // Stellungnahmen abrufen
  const fetchStellungnahmen = useCallback(async () => {
    setLoading(true);
    setError('');

    try {
      const data = await getStellungnahmen(user.id);
    
      if (data.success) {
        // Stellungnahmen direkt setzen und nach Erstellungsdatum sortieren
        const sortedStellungnahmen = (data.stellungnahmen || []).sort((a, b) => 
          new Date(b.erstellt) - new Date(a.erstellt)
        );
        setStellungnahmen(sortedStellungnahmen);
      } else {
        setError(data.error || 'Fehler beim Abrufen der Stellungnahmen');
      }
    } catch (err) {
      setError('Netzwerkfehler: ' + err.message);
    } finally {
      setLoading(false);
    }
  }, [user.id]);

  useEffect(() => {
    fetchStellungnahmen();
  }, [fetchStellungnahmen]);

  // Status-Farben-Funktion
  const getStatusColor = (status) => {
    if (!status) return "";
    
    status = status.toLowerCase();
    if (status.includes('offen') || status.includes('neu')) return "status-new";
    if (status.includes('bearbeitung') || status.includes('in arbeit')) return "status-in-progress";
    if (status.includes('abgeschlossen') || status.includes('erledigt')) return "status-completed";
    if (status.includes('abgelehnt') || status.includes('storniert')) return "status-cancelled";
    return "";
  };

  // Status aktualisieren (für das Dropdown)
  const handleStatusUpdate = async (stellungnahmeId, newStatus) => {
    try {
      const data = await updateStellungnahmeStatus(user.id, stellungnahmeId, newStatus);
      
      if (data.success) {
        // Stellungnahmen neu laden
        fetchStellungnahmen();
      } else {
        setError(data.error || 'Fehler beim Aktualisieren des Status');
      }
    } catch (err) {
      setError('Netzwerkfehler: ' + err.message);
    }
  };

  // Stellungnahme bearbeiten öffnen (nur für Callcenter Leader)
  const handleEditStellungnahme = (stellungnahme) => {
    setSelectedStellungnahme(stellungnahme);
    setShowEditDialog(true);
  };

  // Stellungnahme anschauen öffnen (für Ersteller)
  const handleViewStellungnahme = (stellungnahme) => {
    setSelectedStellungnahme(stellungnahme);
    setShowDetailDialog(true);
  };

  // Stellungnahme-Bearbeitung erfolgreich
  const handleStellungnahmeUpdated = () => {
    setShowEditDialog(false);
    setSelectedStellungnahme(null);
    fetchStellungnahmen();
  };

  // Stellungnahme hinzugefügt
  const handleStellungnahmeAdded = () => {
    setShowForm(false);
    fetchStellungnahmen();
  };

  // Zur Kundenansicht navigieren
  const handleNavigateToCustomer = (customerId) => {
    setShowDetailDialog(false);
    setSelectedStellungnahme(null);
    if (onNavigateToCustomer) {
      onNavigateToCustomer(customerId);
    }
  };

  // Prüfen ob der aktuelle Benutzer der Ersteller einer Stellungnahme ist
  const isCreator = (stellungnahme) => {
    return stellungnahme.user_id === user.id;
  };

  // Bestimmen welche Aktionen für eine Stellungnahme verfügbar sind
  const getAvailableActions = (stellungnahme) => {
    const actions = [];
    
    // Anschauen-Button für Ersteller
    if (isCreator(stellungnahme) || isTeamleader) {
      actions.push('view');
    }

    // Bearbeiten-Button nur für Callcenter Leader
    if (canEditStellungnahme) {
      actions.push('edit');
    }
    
    // Status-Dropdown für alle mit Update-Berechtigung (aber nicht für Callcenter Leader, da sie bearbeiten können)
    if (canUpdateStatus && !canEditStellungnahme) {
      actions.push('status');
    }
    
    return actions;
  };

  // Berechtigung prüfen - Fachberater haben keinen Zugriff auf Stellungnahmen
  if (isFachberater) {
    return (
      <>
        <div className="stellungnahmen-page">
          <div className="no-access">
            <h2>Keine Berechtigung</h2>
            <p>Fachberater haben keinen Zugriff auf die Stellungnahmen-Verwaltung.</p>
          </div>
        </div>
      </>
    );
  }

  return (
    <div className="stellungnahmen-page">
      {showForm ? (
        <StellungnahmeForm
          user={user}
          onCancel={() => setShowForm(false)}
          onStellungnahmeAdded={handleStellungnahmeAdded}
        />
      ) : (
        <>
          <div className="stellungnahmen-header">
            <h2>Stellungnahmen</h2>
            <div className="stellungnahmen-actions">
              <button 
                className="add-stellungnahme-button" 
                onClick={() => setShowForm(true)}
              >
                + Stellungnahme erstellen
              </button>
            </div>
          </div>
        
          {error && <div className="error-message">{error}</div>}
          {loading ? (
            <div className="loading">Stellungnahmen werden geladen...</div>
          ) : stellungnahmen.length === 0 ? (
            <div className="no-data">
              Keine Stellungnahmen gefunden.
            </div>
          ) : (
            <table className="stellungnahmen-table">
              <thead>
                <tr>
                  <th>Erstellt</th>
                  <th>Status</th>
                  <th>Kunde</th>
                  <th>Nachricht</th>
                  <th>Antwort</th>
                  <th>Ersteller</th>
                  <th>Anhang</th>
                  <th>Aktionen</th>
                </tr>
              </thead>
              <tbody>
                {stellungnahmen.map((stellungnahme) => {
                  const availableActions = getAvailableActions(stellungnahme);
                  
                  return (
                    <tr key={stellungnahme.id}>
                      <td style={{ fontSize: '0.75em' }}>
                        {new Date(stellungnahme.erstellt).toLocaleString('de-DE', {
                          dateStyle: 'short',
                          timeStyle: 'short'
                        })}
                      </td>

                      <td>
                        <span className={`status-badge ${getStatusColor(stellungnahme.status)}`}>
                          {stellungnahme.status}
                        </span>
                      </td>

                      <td>
                        <div>
                          <strong>{stellungnahme.kunde_name || 'Unbekannt'}</strong>
                          {stellungnahme.kunde_email && (
                            <div style={{ fontSize: '0.8em', color: '#666' }}>
                              {stellungnahme.kunde_email}
                            </div>
                          )}
                        </div>
                      </td>

                      <td>
                        <div style={{ 
                          maxWidth: '300px',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}>
                          {stellungnahme.nachricht}
                        </div>
                      </td>

                      <td>
                        <div style={{ 
                          maxWidth: '250px',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}>
                          {stellungnahme.antwort ? stellungnahme.antwort : (
                            <span style={{ color: '#999', fontStyle: 'italic' }}>
                              Keine Antwort
                            </span>
                          )}
                        </div>
                      </td>

                      <td>
                        <div>
                          {stellungnahme.ersteller_name || stellungnahme.ersteller_email || 'Unbekannt'}
                          {isCreator(stellungnahme) && (
                            <div style={{ 
                              fontSize: '0.7em', 
                              color: '#007bff', 
                              fontWeight: 'bold',
                              marginTop: '2px'
                            }}>
                            </div>
                          )}
                        </div>
                      </td>

                      <td>
                        {stellungnahme.anhang ? (
                          <span className="status-badge status-completed">
                            📎 Anhang
                          </span>
                        ) : (
                          <span>-</span>
                        )}
                      </td>

                      <td>
                        <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                          {/* Anschauen-Button für Ersteller */}
                          {availableActions.includes('view') && (
                            <button
                              onClick={() => handleViewStellungnahme(stellungnahme)}
                              style={{
                                padding: '4px 8px',
                                fontSize: '0.8em',
                                border: '1px solid #1C86EE',
                                borderRadius: '4px',
                                backgroundColor: '#1C86EE',
                                color: 'white',
                                cursor: 'pointer',
                                whiteSpace: 'nowrap'
                              }}
                            >
                              Anschauen
                            </button>
                          )}
                          
                          {/* Bearbeiten-Button nur für Callcenter Leader */}
                          {availableActions.includes('edit') && (
                            <button
                              onClick={() => handleEditStellungnahme(stellungnahme)}
                              style={{
                                padding: '4px 8px',
                                fontSize: '0.8em',
                                border: '1px solid #4a6de5',
                                borderRadius: '4px',
                                backgroundColor: '#4a6de5',
                                color: 'white',
                                cursor: 'pointer',
                                whiteSpace: 'nowrap'
                              }}
                            >
                              Bearbeiten
                            </button>
                          )}
                          
                          {/* Status-Dropdown für alle mit Update-Berechtigung (außer Callcenter Leader) */}
                          {availableActions.includes('status') && (
                            <select
                              value={stellungnahme.status}
                              onChange={(e) => handleStatusUpdate(stellungnahme.id, e.target.value)}
                              style={{
                                padding: '4px',
                                fontSize: '0.8em',
                                border: '1px solid #ccc',
                                borderRadius: '4px'
                              }}
                            >
                              <option value="Offen">Offen</option>
                              <option value="In Bearbeitung">In Bearbeitung</option>
                              <option value="Abgeschlossen">Abgeschlossen</option>
                              <option value="Abgelehnt">Abgelehnt</option>
                            </select>
                          )}
                          
                          {/* Fallback wenn keine Aktionen verfügbar sind */}
                          {availableActions.length === 0 && (
                            <span style={{ fontSize: '0.8em', color: '#999', fontStyle: 'italic' }}>
                              Keine Aktionen
                            </span>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </>
      )}
      
      {/* Bearbeitungs-Dialog für Callcenter Leader */}
      {showEditDialog && selectedStellungnahme && (
        <StellungnahmeEditDialog
          stellungnahme={selectedStellungnahme}
          user={user}
          onSave={handleStellungnahmeUpdated}
          onCancel={() => {
            setShowEditDialog(false);
            setSelectedStellungnahme(null);
          }}
        />
      )}
      
      {/* Detail-Dialog für Ersteller */}
      {showDetailDialog && selectedStellungnahme && (
        <StellungnahmeDetailDialog
          stellungnahme={selectedStellungnahme}
          user={user}
          onClose={() => {
            setShowDetailDialog(false);
            setSelectedStellungnahme(null);
          }}
          onNavigateToCustomer={handleNavigateToCustomer}
        />
      )}
      
      <style jsx>{`
        .stellungnahmen-page {
          padding: 20px;
          max-width: 1400px;
          margin: 0 auto;
        }

        .stellungnahmen-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 20px;
          padding-bottom: 10px;
          border-bottom: 2px solid #e2e8f0;
        }

        .stellungnahmen-header h2 {
          margin: 0;
          color: #2d3748;
          font-size: 1.8rem;
        }

        .stellungnahmen-actions {
          display: flex;
          gap: 10px;
        }

        .add-stellungnahme-button {
          background: #4a6de5;
          color: white;
          border: none;
          border-radius: 6px;
          padding: 10px 20px;
          cursor: pointer;
          font-weight: 500;
          transition: background-color 0.2s;
        }

        .add-stellungnahme-button:hover {
          background: #3c5ce0;
        }

        .stellungnahmen-table {
          width: 100%;
          border-collapse: collapse;
          background: white;
          border-radius: 8px;
          overflow: hidden;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
        }

        .stellungnahmen-table th {
          background: #f8fafc;
          padding: 12px;
          text-align: left;
          font-weight: 600;
          color: #4a5568;
          border-bottom: 1px solid #e2e8f0;
        }

        .stellungnahmen-table td {
          padding: 12px;
          border-bottom: 1px solid #f1f5f9;
          vertical-align: top;
        }

        .stellungnahmen-table tr:hover {
          background: #f7fafc;
        }

        .status-badge {
          display: inline-block;
          padding: 4px 8px;
          border-radius: 12px;
          font-size: 0.75rem;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .status-new {
          background: #fef3c7;
          color: #d97706;
        }

        .status-in-progress {
          background: #dbeafe;
          color: #1d4ed8;
        }

        .status-completed {
          background: #d1fae5;
          color: #059669;
        }

        .status-cancelled {
          background: #fee2e2;
          color: #dc2626;
        }

        .error-message {
          background: #fee2e2;
          color: #dc2626;
          padding: 12px;
          border-radius: 6px;
          margin-bottom: 20px;
          border-left: 4px solid #dc2626;
        }

        .loading {
          text-align: center;
          padding: 40px;
          color: #718096;
          font-size: 1.1rem;
        }

        .no-data {
          text-align: center;
          padding: 40px;
          color: #a0aec0;
          font-style: italic;
        }

        .no-access {
          text-align: center;
          padding: 60px 20px;
          background: white;
          border-radius: 8px;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
        }

        .no-access h2 {
          color: #e53e3e;
          margin-bottom: 16px;
        }

        .no-access p {
          color: #718096;
          font-size: 1.1rem;
        }

        @media (max-width: 768px) {
          .stellungnahmen-page {
            padding: 10px;
          }

          .stellungnahmen-header {
            flex-direction: column;
            gap: 15px;
            align-items: flex-start;
          }

          .stellungnahmen-table {
            font-size: 0.9rem;
          }

          .stellungnahmen-table th,
          .stellungnahmen-table td {
            padding: 8px;
          }
        }
      `}</style>
    </div>
  );

}

export default Stellungnahmen;